import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePubSub } from "@huub-web/ui/hooks/usePubSub";
import { useDelayedState } from "@huub-web/ui/hooks/useDelayedState";
import { useDebug } from "@huub-web/ui";

import type { TemplateCollection } from "../types";
import { useTemplates } from "./useTemplates";

/**
 * Hook for managing asynchronous template fetching and refreshing
 *
 * @param endpoint - The API endpoint to fetch templates from
 * @param id - Optional ID for subscribing to refresh events
 * @returns Object containing loading state, error state, templates data, and hasLoadedOnce flag
 */
export const useAsyncTemplates = (
  endpoint?: string,
  id?: string,
  enabled = true,
) => {
  const { fetchTemplates } = useTemplates();
  const pubSub = usePubSub();
  const { t } = useTranslation();
  const { log } = useDebug();

  const [loading, setLoading, setImmediateLoading] = useDelayedState<boolean>(
    true,
    500,
  );
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [templatesData, setTemplatesData] = useState<TemplateCollection | null>(
    null,
  );
  const [refreshCount, setRefreshCount] = useState(0);

  const isInitializedEndpointRef = useRef<string>("");
  const isInitializedRefreshCountRef = useRef(0);

  const previousEnabledRef = useRef(enabled);

  useEffect(() => {
    const wasEnabled = previousEnabledRef.current;
    previousEnabledRef.current = enabled;

    if (!wasEnabled && enabled && Boolean(isInitializedEndpointRef.current)) {
      setRefreshCount((prev) => prev + 1);
    }
  }, [enabled]);

  useEffect(() => {
    if (
      !enabled ||
      !endpoint ||
      (isInitializedEndpointRef.current === endpoint &&
        isInitializedRefreshCountRef.current === refreshCount)
    ) {
      return;
    }

    setImmediateLoading(true);
    setError(null);

    isInitializedEndpointRef.current = endpoint;
    isInitializedRefreshCountRef.current = refreshCount;

    const fetchData = async () => {
      if (!endpoint) {
        console.warn("[useAsyncTemplates] No endpoint provided");
        return;
      }
      log("orange", "useAsyncTemplates", `Fetching templates '${endpoint}'`);

      setLoading(true);
      setTimeout(() => {
        fetchTemplates(endpoint)
          .then((data) => {
            setTemplatesData(data);
          })
          .catch((error) => {
            console.error(error);
            setError(t("general.error"));
          })
          .finally(() => {
            setLoading(false);
          });
      }, 1000);
    };

    fetchData();
  }, [endpoint, refreshCount, enabled]);

  // Refresh templates
  // ----------------

  useEffect(() => {
    if (!pubSub || !id || !enabled) {
      if (!id) {
        console.warn("[useAsyncTemplates] (refreshTemplates) No ID provided");
      }
      return;
    }

    const unsubscribe = pubSub.subscribe(
      "refreshTemplates",
      ({ id: templateID }) => {
        if (!id || !templateID || templateID !== id) {
          return;
        }
        log(
          "cyan",
          ["useAsyncTemplates", "refreshTemplates"],
          `refreshing ${templateID}`,
        );
        setRefreshCount((prev) => prev + 1);
      },
    );

    return () => unsubscribe();
  }, [pubSub, id, enabled]);

  const clear = () => {
    setTemplatesData(null);
    setLoading(true);
    setHasLoadedOnce(false);
  };

  useEffect(() => {
    if (!loading) {
      setHasLoadedOnce(true);
    }
  }, [loading]);

  return {
    loading,
    hasLoadedOnce,
    error,
    templatesData,
    clear,
  };
};
